import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import { useSetParticipantNoteMutation, type ParticipantSummary } from '@proaxiv/server-api'
import { formatFullName } from '../utils/participantMappers'
import { Button } from './Button'

/** Server-enforced max length for the note text — kept in sync with the backend cap. */
const NOTE_MAX_LENGTH = 2000

// ── Icons ──────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  participant: ParticipantSummary
  onDone: () => void
  onCancel: () => void
}

/**
 * Modal for the organizer to write or edit a per-participant private note.
 * The note is persisted on the backend (`organizerNote`) and shown on that participant's
 * ticket page. It is invisible to other participants, and every save/clear is logged to the
 * participant's conversation history (#158). The "send notification" checkbox (checked by
 * default) controls whether the participant is emailed that a note was added or updated —
 * the email links to their ticket page but never includes the note text itself.
 */
export function PrivateNoteModal({ participant, onDone, onCancel }: Props) {
  const { t } = useTranslation()
  const [setParticipantNote, { isLoading }] = useSetParticipantNoteMutation()

  const participantName = formatFullName(participant.firstName, participant.lastName)
  const [note, setNote] = useState(participant.organizerNote ?? '')
  const [notify, setNotify] = useState(true)

  async function handleSave() {
    const result = await setParticipantNote({
      participantId: participant.id!,
      setParticipantNoteBody: { note, sendEmailNotification: notify },
    })
    if ('error' in result) return
    onDone()
  }

  return (
    <Modal labelledBy="note-modal-title">
      <div className="border-pax-purple/50 flex w-full max-w-lg flex-col rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
        {/* Header */}
        <div className="border-pax-purple/20 flex items-center justify-between border-b px-6 py-4">
          <h2 id="note-modal-title" className="text-pax-black text-lg font-bold">
            {t('note_modal_title', { name: participantName })}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-pax-gray hover:text-pax-black cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
            aria-label={t('btn_close')}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-6 py-5">
          <p className="text-pax-gray text-sm">{t('note_modal_subtitle')}</p>

          <div className="flex flex-col gap-1.5">
            <p className="text-pax-black text-xs font-medium">{participant.email}</p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
              placeholder={t('note_modal_placeholder')}
              rows={5}
              maxLength={NOTE_MAX_LENGTH}
              className="border-pax-purple/30 text-pax-black placeholder:text-pax-gray/50 focus:border-pax-blue w-full resize-none rounded-xl border bg-white px-3 py-2.5 text-sm transition-colors outline-none"
              aria-label={t('note_modal_placeholder')}
            />
            <p className="text-pax-gray text-xs">
              {t('note_modal_char_limit_hint', { max: NOTE_MAX_LENGTH })}
            </p>
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 text-sm select-none">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="accent-pax-blue h-4 w-4 cursor-pointer"
            />
            <span className="text-pax-black">{t('send_notification')}</span>
          </label>
        </div>

        {/* Footer */}
        <div className="border-pax-purple/20 flex items-center justify-end gap-3 border-t px-6 py-4">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={isLoading}>
            {t('btn_cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={isLoading}
          >
            {t('note_modal_save')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
